import React, { useEffect, useRef } from 'react';
import { CSVLink } from 'react-csv';
import { withRouter } from 'react-router';

import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';

import EntityListFrame from 'src/components/layouts/EntityListFrame';
import DataContentTable from 'src/components/parts/DataContentTable';
import DataTableFilter from 'src/components/parts/DataTableFilter/DataTableFilter';
import FeatureDisabledMessage from 'src/components/parts/FeatureDisabledMessage';
import useCatchAPIError from 'src/hooks/useCatchAPIError';
import useCustomerAccountFeatures from 'src/hooks/useCustomerAccountFeatures';
import useGlobalLoadingBar from 'src/hooks/useGlobalLoadingBar';
import apiService from 'src/services/api.service';
import csvTableService from 'src/services/csv.table.service';
import helperService from 'src/services/help.service';
import pdfTableService from 'src/services/pdf.table.service';
import ExportMenu from 'src/wrappers/ExportMenu';

const useStyles = makeStyles((theme) => ({
  titleArea: {
    marginBottom: '28px',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
}));

const headCells = [
  {
    id: 'Supplier',
    numeric: false,
    disablePadding: true,
    label: 'Supplier',
  },
  {
    id: 'Amount',
    numeric: true,
    disablePadding: false,
    label: 'Total Spend ($)',
    tooltip: 'Total Charges billed to you by Verus',
  },
];

function SpendPerSupplierTable(props) {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('Supplier');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [rows, setRows] = React.useState('');
  const [filter, setFilter] = React.useState();
  const [totalNumberOfRows, setTotalNumberOfRows] = React.useState(0);
  const [downloadCsv, setDownloadCsv] = React.useState({ headers: [], data: [], filename: '' });
  const csvLinkEl = useRef();
  const { enabledSpendPerSuppliers } = useCustomerAccountFeatures();
  const { catchApiError } = useCatchAPIError();
  const { startProgress, stopProgress } = useGlobalLoadingBar();

  useEffect(() => {
    if (filter) {
      startProgress();
      getData();
    }
  }, [rowsPerPage, page, filter]);

  const classes = useStyles();

  const filterCallback = (data) => {
    setPage(0);
    setFilter(data);
  };

  const getData = (inData) => {
    let data = inData !== undefined ? inData : filter;
    apiService
      .getSpendPerSupplierTable(data.accountId, data.start, data.end, order, orderBy, page * rowsPerPage, rowsPerPage, data.freeTextSearch)
      .then((result) => {
        setRows(createTableListData(result.data.items));
        setTotalNumberOfRows(result.data.count);
        stopProgress();
      })
      .catch(catchApiError);
  };

  const createTableListData = (items) => {
    let newArray = [];
    var index = 0;
    items.map((item) => {
      let cells = [index++, item.supplier, helperService.thousands_separators(item.amount)];

      let tmpCell = {};
      tmpCell['id'] = item.id;
      headCells.forEach((x, index) => {
        tmpCell[x.id] = cells[index + 1];
      });
      newArray.push(tmpCell);
    });
    return newArray;
  };
  const handleRequestSort = (sortOrder, property) => {
    setOrder(sortOrder);
    setOrderBy(property);
    getData();
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newValue) => {
    setPage(0);
    setRowsPerPage(newValue);
  };

  const generatePdf = () => {
    if (totalNumberOfRows > 0) {
      apiService
        .getSpendPerSupplierTable(filter.accountId, filter.start, filter.end, order, orderBy, 0, totalNumberOfRows, filter.freeTextSearch)
        .then((result) => {
          stopProgress();
          pdfTableService.generateSpendPerSupplierPDF(
            result.data.items,
            'Spend Per Supplier     Company:' + filter.accountName + '    Period:' + filter.periodName,
            'Spend Per Supplier Report'
          );
        })
        .catch(catchApiError);
    }
  };

  const generateCSV = () => {
    if (totalNumberOfRows > 0) {
      apiService
        .getSpendPerSupplierTable(filter.accountId, filter.start, filter.end, order, orderBy, 0, totalNumberOfRows, filter.freeTextSearch)
        .then((result) => {
          stopProgress();
          var csvData = csvTableService.generateSpendPerSupplierCsvData(result.data.items, filter.accountName + '_' + filter.periodName);
          setDownloadCsv(csvData);
          csvLinkEl.current.link.click();
        })
        .catch(catchApiError);
    }
  };

  return (
    <>
      <CSVLink headers={downloadCsv.headers} filename={downloadCsv.filename} data={downloadCsv.data} ref={csvLinkEl} />
      <EntityListFrame>
        {enabledSpendPerSuppliers ? (
          <>
            <Grid container className={classes.titleArea}>
              <Grid item xs={12} sm className="align-self-center">
                <h4>Spend Per Supplier</h4>
              </Grid>
              <Grid item className={'align-self-center'}>
                <ExportMenu onPdfClick={generatePdf} onCsvClick={generateCSV} />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <DataTableFilter sendFilterComponentData={filterCallback} showDateBasedOn={false} />
            </Grid>
            <Divider />

            <Grid item xs={12}>
              {rows && (
                <DataContentTable
                  title=""
                  headCells={headCells}
                  totalNumberOfRows={totalNumberOfRows}
                  rows={rows}
                  page={page}
                  handleRequestSort={handleRequestSort}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
              )}
            </Grid>
          </>
        ) : (
          <FeatureDisabledMessage />
        )}
      </EntityListFrame>
    </>
  );
}

export default withRouter(SpendPerSupplierTable);
